import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Divider from 'LuxKit/Divider'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useState } from 'react'
import styled from 'styled-components'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineWrenchIcon from 'LuxKit/Icons/line/LineWrenchIcon'
import LineBackpackIcon from 'LuxKit/Icons/line/LineBackpackIcon'
import LineKeySkeletonAltIcon from 'LuxKit/Icons/line/LineKeySkeletonAltIcon'
import LineMedicalSquareIcon from 'LuxKit/Icons/line/LineMedicalSquareIcon'
import LineFileCheckAltIcon from 'LuxKit/Icons/line/LineFileCheckAltIcon'
import LinePumpIcon from 'LuxKit/Icons/line/LinePumpIcon'
import { FormattedMessage, useIntl } from 'react-intl'

const TabContent = styled(Group)`
  min-height: ${rem(275)};
  padding-top: ${rem(16)};
`

const StyledList = styled(List)`
  ol {
    margin: 0;
  }
`

const StyledBodyTextBlock = styled(BodyTextBlock)`
  white-space: pre-line;
`

interface Props {
  carHireSupplier: string
  insuranceQuoteInfo: App.CarHIreOfferInsuranceQuoteInformation
  hasEnhancedProtection: boolean
}

function CarHireInsuranceEnhancedProtectionInfo(props: Props) {
  const { carHireSupplier, insuranceQuoteInfo: insuranceInfo, hasEnhancedProtection } = props

  const intl = useIntl()

  const tabLabels: Record<string, string> = {
    covered: intl.formatMessage({
      id: 'wUWPxVhbna',
      defaultMessage: "What's covered",
      description: 'Tab label for the coverage details tab in car hire enhanced protection modal',
    }),
    thingsToKnow: intl.formatMessage({
      id: 'JitMynYDG8',
      defaultMessage: 'Things to know',
      description: 'Tab label for the things to know tab in car hire enhanced protection modal',
    }),
  }

  const tabIds = ['covered', 'thingsToKnow'] as const
  const [activeTab, setActiveTab] = useState<string>(tabIds[0])

  const refundableExcessLabel = intl.formatMessage({
    id: 'rJBfN4NQUh',
    defaultMessage: '100% Refundable Excess',
    description: 'Heading for the refundable excess section in car hire enhanced protection',
  })

  const termsAndConditionsLabel = intl.formatMessage({
    id: 'jgv1yx235q',
    defaultMessage: 'Terms & Conditions',
    description: 'Link text for car hire enhanced protection terms and conditions',
  })

  const creditCardTip = intl.formatMessage({
    id: 'va9bFVqz1n',
    defaultMessage: "Make sure you have a credit or debit card in the main driver's name and enough credit to hold the refundable deposit at the rental desk.",
    description: 'Tip about credit card requirement at car hire rental desk',
  })

  const insuranceAtDeskTip = intl.formatMessage(
    {
      id: 'FnNYMpwxoc',
      defaultMessage: '{carHireSupplier} may ask you to buy insurance at the rental desk. If you add Enhanced Protection you can refuse insurance at the rental desk with confidence.',
      description: 'Tip about refusing insurance at car hire rental desk when enhanced protection is selected',
    },
    { carHireSupplier },
  )

  const accidentBannerHeading = intl.formatMessage({
    id: 'XVS+1b7rA6',
    defaultMessage: "If there's an accident, here's what to expect",
    description: 'Warning banner heading about car hire accident procedure',
  })

  // AU & rest of world coverage items
  const auThirdPartyTitle = intl.formatMessage({
    id: 'N16U06iyGk',
    defaultMessage: 'Third Party Protection',
    description: 'Coverage item title for third party protection in car hire enhanced protection (AU/world)',
  })

  const auThirdPartyText = intl.formatMessage({
    id: '7I9tjnI3SJ',
    defaultMessage: 'Included as part of your rental agreement. If you or any authorised driver are involved in an accident, Third Party injury and property damage is already covered.',
    description: 'Coverage item description for third party protection in car hire enhanced protection (AU/world)',
  })

  const auAccidentsTitle = intl.formatMessage({
    id: '0b7BeXBkFF',
    defaultMessage: 'Accidents, theft & damage cover',
    description: 'Coverage item title for accidents, theft and damage in car hire enhanced protection (AU/world)',
  })

  const auAccidentsText = intl.formatMessage({
    id: '88tiAa38tn',
    defaultMessage: "Our policy includes coverage for theft, as well as damage to the rental car's windscreen, glass, undercarraige, roof and tyres. Damage relating to road traffic or weather-related accidents, collision with animals, fire or explosion are also covered. If your car needs to be towed as the result of an accident, you're covered too.",
    description: 'Coverage item description for accidents, theft and damage in car hire enhanced protection (AU/world)',
  })

  const auPersonalPossessionsTitle = intl.formatMessage({
    id: 'jzxDKSJWf8',
    defaultMessage: 'Personal possessions in car',
    description: 'Coverage item title for personal possessions in car hire enhanced protection (AU/world)',
  })

  const auPersonalPossessionsText = intl.formatMessage({
    id: 'IfnfwpxHbe',
    defaultMessage: "We safeguard your personal items against damage or theft in the rental car's locked areas.",
    description: 'Coverage item description for personal possessions in car hire enhanced protection (AU/world)',
  })

  const auLostKeysTitle = intl.formatMessage({
    id: 'NQ3e75ezLl',
    defaultMessage: 'Lost keys & personal possessions',
    description: 'Coverage item title for lost keys in car hire enhanced protection (AU/world)',
  })

  const auLostKeysText = intl.formatMessage({
    id: 'KgVaU1UWQQ',
    defaultMessage: 'We cover lost keys and stolen personal items from your car.',
    description: 'Coverage item description for lost keys in car hire enhanced protection (AU/world)',
  })

  const auMedicalTitle = intl.formatMessage({
    id: '6A7gQK/r24',
    defaultMessage: 'Medical & accidental death coverage',
    description: 'Coverage item title for medical coverage in car hire enhanced protection (AU/world)',
  })

  const auMedicalText = intl.formatMessage({
    id: 's16i5FuClO',
    defaultMessage: 'Our policy includes coverage for car-related accidents or death.',
    description: 'Coverage item description for medical coverage in car hire enhanced protection (AU/world)',
  })

  const auPreBookedTitle = intl.formatMessage({
    id: '9jZFIoMElT',
    defaultMessage: 'Pre-booked travel arrangements',
    description: 'Coverage item title for pre-booked travel in car hire enhanced protection (AU/world)',
  })

  const auPreBookedText = intl.formatMessage({
    id: '1mVsrSPLS8',
    defaultMessage: "We've got you covered if your rental car experiences issues like inoperability, theft, or accidents that disrupt your pre-booked travel arrangements.",
    description: 'Coverage item description for pre-booked travel in car hire enhanced protection (AU/world)',
  })

  // US destination coverage items
  const usThirdPartyTitle = intl.formatMessage({
    id: '5Qel/K7E+m',
    defaultMessage: 'Third Party Protection',
    description: 'Coverage item title for third party protection in car hire enhanced protection (US)',
  })

  const usThirdPartyText = intl.formatMessage({
    id: 'yYL+FB2a6e',
    defaultMessage: 'Included as part of your rental agreement. If you or any authorised driver are involved in an accident, Third Party injury and property damage is already covered.',
    description: 'Coverage item description for third party protection in car hire enhanced protection (US)',
  })

  const usAccidentsTitle = intl.formatMessage({
    id: '1IYrNDIXNC',
    defaultMessage: 'Accidents, theft & damage cover',
    description: 'Coverage item title for accidents, theft and damage in car hire enhanced protection (US)',
  })

  const usAccidentsText = intl.formatMessage({
    id: 'So+2yflCG5',
    defaultMessage: "Accidental Damage or Theft, including damage to a third party not covered by the rental company relating to damage or theft of the vehicle.\nThis could include damage to your rental car's windscreen, glass, undercarriage, roof and tyres.\nIf your car needs to be towed as a result of an accident you are covered too.",
    description: 'Coverage item description for accidents, theft and damage in car hire enhanced protection (US)',
  })

  const usLostKeysTitle = intl.formatMessage({
    id: '6+BCcPZ+yq',
    defaultMessage: 'Lost keys & personal possessions in car',
    description: 'Coverage item title for lost keys and possessions in car hire enhanced protection (US)',
  })

  const usLostKeysText = intl.formatMessage({
    id: 'wT6RL2K1fh',
    defaultMessage: 'We cover lost keys and stolen personal items from your car.',
    description: 'Coverage item description for lost keys and possessions in car hire enhanced protection (US)',
  })

  const usMisfuellingTitle = intl.formatMessage({
    id: 'Lmv7Yz1RJr',
    defaultMessage: 'Misfuelling cover',
    description: 'Coverage item title for misfuelling cover in car hire enhanced protection (US)',
  })

  const usMisfuellingText = intl.formatMessage({
    id: '5C35purw2I',
    defaultMessage: 'We cover against the costs incurred as a result of You putting the wrong type of fuel into a rental vehicle.',
    description: 'Coverage item description for misfuelling cover in car hire enhanced protection (US)',
  })

  const usAdminFeesTitle = intl.formatMessage({
    id: '6hC69vr0fQ',
    defaultMessage: 'Administrative fees',
    description: 'Coverage item title for administrative fees in car hire enhanced protection (US)',
  })

  const usAdminFeesText = intl.formatMessage({
    id: 'rg73XdrIdc',
    defaultMessage: 'We cover against administration charges applied by a rental company which cannot be recovered following a claim which is covered by this insurance.',
    description: 'Coverage item description for administrative fees in car hire enhanced protection (US)',
  })

  const usUnusedDaysTitle = intl.formatMessage({
    id: 'lEbh3YE9TF',
    defaultMessage: 'Unused rental days',
    description: 'Coverage item title for unused rental days in car hire enhanced protection (US)',
  })

  const usUnusedDaysText = intl.formatMessage({
    id: '+iFKd/VraO',
    defaultMessage: 'You are eligible for a refund if the car rental agreement is cancelled or cut short due to a medical condition or accident',
    description: 'Coverage item description for unused rental days in car hire enhanced protection (US)',
  })

  const coveredItemsAUAndRestOfTheWorld = [
    {
      title: auThirdPartyTitle,
      text: auThirdPartyText,
      icon: <LineShieldCheckIcon />,
    },
    {
      title: auAccidentsTitle,
      text: auAccidentsText,
      icon: <LineWrenchIcon />,
    },
    {
      title: auPersonalPossessionsTitle,
      text: auPersonalPossessionsText,
      icon: <LineBackpackIcon />,
    },
    {
      title: auLostKeysTitle,
      text: auLostKeysText,
      icon: <LineKeySkeletonAltIcon />,
    },
    {
      title: auMedicalTitle,
      text: auMedicalText,
      icon: <LineMedicalSquareIcon />,
    },
    {
      title: auPreBookedTitle,
      text: auPreBookedText,
      icon: <LineFileCheckAltIcon />,
    },
  ]

  const coveredItemsUSDestination = [
    {
      title: usThirdPartyTitle,
      text: usThirdPartyText,
      icon: <LineShieldCheckIcon />,
    },
    {
      title: usAccidentsTitle,
      text: usAccidentsText,
      icon: <LineWrenchIcon />,
    },
    {
      title: usLostKeysTitle,
      text: usLostKeysText,
      icon: <LineBackpackIcon />,
    },
    {
      title: usMisfuellingTitle,
      text: usMisfuellingText,
      icon: <LinePumpIcon />,
    },
    {
      title: usAdminFeesTitle,
      text: usAdminFeesText,
      icon: <LineShieldCheckIcon />,
    },
    {
      title: usUnusedDaysTitle,
      text: usUnusedDaysText,
      icon: <LineFileCheckAltIcon />,
    },
  ]

  return (
    <>
      <TabButtonsGroup>
        {tabIds.map((tabId) => (
          <TabButton
            isActive={tabId === activeTab}
            key={tabId}
            size="medium"
            onClick={() => setActiveTab(tabId)}
          >
            {tabLabels[tabId]}
          </TabButton>
        ))}
      </TabButtonsGroup>
      {insuranceInfo &&
        <VerticalSpacer gap={20}>
          <TabContent direction="vertical" gap={16}>
            {activeTab === 'covered' &&
              <>
                {!hasEnhancedProtection && <>
                  <BodyTextBlock startIcon={(<LineCheckCircleIcon />)} variant="medium" weight="bold">{refundableExcessLabel}</BodyTextBlock>
                  <BodyTextBlock variant="medium">
                    <FormattedMessage
                      id="gZJqRjJmn7"
                      defaultMessage="The policy will reimburse the excess you need to pay when there’s an incident with your rental vehicle. In the case of damage to your rental car, your excess (<b>up to {policyLimit}</b>) will be fully refunded by AWN insurance."
                      description="Description of the refundable excess policy for car hire enhanced protection"
                      values={{
                        policyLimit: <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} />,
                        b: (chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </BodyTextBlock>
                  <Divider kind="primary" />
                </>}

                {(hasEnhancedProtection ? coveredItemsUSDestination : coveredItemsAUAndRestOfTheWorld).map((item, index, arr) => <>
                  <BodyTextBlock startIcon={item.icon} variant="medium" weight="bold">{item.title}</BodyTextBlock>
                  <StyledBodyTextBlock style={{ whiteSpace: 'pre-line' }} variant="medium">{item.text}</StyledBodyTextBlock>
                  {index + 1 !== arr.length &&
                    <Divider kind="primary" />}
                </>)}
              </>}

            {activeTab === 'thingsToKnow' && insuranceInfo?.policyLimit > 0 &&
              <>
                <List spaced>
                  <li>{creditCardTip}</li>
                  <li>{insuranceAtDeskTip}</li>
                  <li>
                    <FormattedMessage
                      id="QbZd7FBAIb"
                      defaultMessage="Enhanced Protection covers up to a maximum total policy limit of {policyLimit} if there is damage or theft of the car during the rental period. When you book a car and add Enhanced Protection, Limited cover is also included."
                      description="Information about enhanced protection policy limit for car hire"
                      values={{
                        policyLimit: <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} />,
                      }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="1r3h/QfuSP"
                      defaultMessage="<b>AWN Insurance</b> will refund you if you need to cancel Enhanced Protection anytime prior to pick up"
                      description="Information about AWN Insurance refund policy for car hire enhanced protection"
                      values={{
                        b: (chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </li>
                </List>
                <MessageBanner
                  kind="warning"
                  heading={accidentBannerHeading}
                  icon={<LineExclamationTriangleIcon />}
                >
                  <StyledList type="unstyled">
                    <ol>
                      <FormattedMessage
                        id="1XwRxgPaWi"
                        defaultMessage="1. <b>{carHireSupplier} </b> will charge you the cost of damage, up to {policyLimit}"
                        description="Step 1 of car hire accident procedure: supplier charges damage cost"
                        values={{
                          carHireSupplier,
                          policyLimit: <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} />,
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </ol>
                    <ol>
                      <FormattedMessage
                        id="1GXytJ/W2q"
                        defaultMessage="2. You can claim the amount you have been charged from <b>AWN Insurance</b>"
                        description="Step 2 of car hire accident procedure: claim from AWN Insurance"
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </ol>
                    <ol>
                      <FormattedMessage
                        id="c5XCmxBKJ4"
                        defaultMessage="3. Once the claim is approved, <b>AWN Insurance</b> will refund you the amount you paid"
                        description="Step 3 of car hire accident procedure: AWN Insurance refund"
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </ol>
                  </StyledList>
                </MessageBanner>
              </>}
          </TabContent>
          <TextLink href={insuranceInfo.termsAndConditionsUrl} target="_blank">{termsAndConditionsLabel}</TextLink>
        </VerticalSpacer>}
    </>
  )
}

export default CarHireInsuranceEnhancedProtectionInfo
